import React, { useEffect, useState } from 'react';
import { BubbleButton, BubbleFormGroup, BubbleInput, BubbleTypeahead } from '../BubbleUI';
import {
  Brand,
  BrandAttribute,
  BrandDayTheme,
  BrandEventHighlight,
  BrandFontSize,
  BrandGroupType,
  BrandImageType,
  BrandLevel,
  BrandSegment,
  BrandSettings,
  BrandVenueType,
  BrandTimeGrouping,
} from '@go-shared/types/brand';
import { updateBrand, uploadBrandLogo } from '@go-client/lib/api/brand';
import { createPortal } from 'react-dom';
import BubbleFileM from '../BubbleUI/BubbleFileM';
import { VenueRequestFile } from '@go-shared/types/groupBooking';
import { lastEl } from '@go-shared/lib/functional';
import { ObjectId } from 'bson';
import { RequiredBy } from '@go-shared/types/typeUtils';
import { BrandMealPeriod } from '@go-shared/lib/mealPeriods';
import { RUNDOWN_DEFAULT_FONT_SIZE, RUNDOWN_LIMIT_FONT_SIZE } from '@go-shared/lib/property';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { evaluateMcmEndpoint, mcmEndpointToName, toggleMcmEndpoint } from '@go-shared/lib/mcm';
import { mcmEndpointToIcon } from '@go-client/lib/mcm';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface BrandFormDataBase {
  name: string;
  venueTypes: BrandVenueType[];
  segments: BrandSegment[];
  levels: BrandLevel[];
  attributes: BrandAttribute[];
  settings: RequiredBy<BrandSettings, 'notes'>;
  groupTypes: BrandGroupType[];
  mealPeriods: BrandMealPeriod[];
  dayThemes: BrandDayTheme[];
  timeGroupings: BrandTimeGrouping[];
  imageTypes: BrandImageType[];
  eventHighlights: BrandEventHighlight[];
  fontSize: BrandFontSize;
}

type BrandFormData = BrandFormDataBase & {
  _dirty: boolean;
  _errors: Partial<BrandFormError>;
};

type BrandFormError = { [K in keyof BrandFormDataBase]: string };

function DEFAULT_FORM_DATA(brand?: Brand): BrandFormData {
  return {
    name: brand?.name ?? '',
    venueTypes: brand?.venueTypes ?? [],
    segments: brand?.segments ?? [],
    levels: brand?.levels ?? [],
    attributes: brand?.attributes ?? [],
    settings: {
      ...brand?.settings,
      notes: brand?.settings?.notes ?? [],
    },
    groupTypes: brand?.groupTypes ?? [],
    mealPeriods: brand?.mealPeriods ?? [],
    dayThemes: brand?.dayThemes ?? [],
    timeGroupings: brand?.timeGroupings ?? [],
    imageTypes: brand?.imageTypes ?? [],
    eventHighlights: brand?.eventHighlights ?? [],
    _dirty: false,
    _errors: {},
    fontSize: brand?.fontSize ?? {
      min: RUNDOWN_DEFAULT_FONT_SIZE.min,
      max: RUNDOWN_DEFAULT_FONT_SIZE.max,
    },
  };
}

interface BrandFormProps {
  edit: Brand;
  onClose?: () => void;
  actionsElem?: HTMLDivElement;
  onFormSubmitted: () => Promise<void>;
}

export default function BrandForm(props: BrandFormProps) {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<BrandFormData>(DEFAULT_FORM_DATA(props.edit));
  const serverInfo = useServerInfo();

  useEffect(() => {
    resetForm();
  }, [props.edit]);

  function resetForm() {
    setFormData(DEFAULT_FORM_DATA(props.edit));
  }

  function changeForm<T extends keyof BrandFormDataBase>(key: T, val: BrandFormDataBase[T]) {
    setFormData({ ...formData, [key]: val, _dirty: true });
  }

  async function submitForm() {
    setSaving(true);
    try {
      if (props.edit) {
        // user intends to edit an existing property
        await updateBrand(props.edit._id, {
          ...props.edit,
          name: formData.name,
          venueTypes: formData.venueTypes,
          segments: formData.segments,
          levels: formData.levels,
          attributes: formData.attributes,
          settings: formData.settings,
          groupTypes: formData.groupTypes,
          mealPeriods: formData.mealPeriods,
          dayThemes: formData.dayThemes,
          timeGroupings: formData.timeGroupings,
          imageTypes: formData.imageTypes,
          eventHighlights: formData.eventHighlights,
          fontSize: formData.fontSize,
        });
      }

      await props.onFormSubmitted();
      if (props.onClose) props.onClose();
    } finally {
      setSaving(false);
    }
  }

  const errored = Object.keys(formData._errors).length > 0;

  const files: VenueRequestFile[] = props.edit.logo
    ? [
        {
          _id: '123',
          name: lastEl(props.edit.logo.split('/')),
          location: props.edit.logo,
        },
      ]
    : [];

  async function onUploadFile(file: File) {
    await uploadBrandLogo(props.edit._id, file);
    await props.onFormSubmitted();
  }

  const venueTypeOpts = formData.venueTypes.map(t => ({
    key: t._id,
    refKey: t._id,
    item: t,
  }));

  const segmentOpts = formData.segments.map(t => ({
    key: t._id,
    refKey: t._id,
    item: t,
  }));

  const levelOpts = formData.levels.map(t => ({
    key: t._id,
    refKey: t._id,
    item: t,
  }));

  const attributeOpts = formData.attributes.map(t => ({
    key: t._id,
    refKey: t._id,
    item: t,
  }));

  const notesOpts = formData.settings.notes.map(t => ({
    key: t._id,
    refKey: t._id,
    item: t,
  }));

  const groupTypeOpts = formData.groupTypes.map(t => ({
    key: t._id,
    refKey: t._id,
    item: t,
  }));

  const mealPeriods = formData.mealPeriods.map(t => ({
    key: t._id,
    refKey: t._id,
    item: t,
  }));

  const dayThemeOpts = formData.dayThemes.map(t => ({
    key: t._id,
    refKey: t._id,
    item: t,
  }));

  const timeGroupingOpts = formData.timeGroupings.map(t => ({
    key: t._id,
    refKey: t._id,
    item: t,
  }));

  const imageTypesOpts = formData.imageTypes.map(t => ({
    key: t._id,
    refKey: t._id,
    item: t,
  }));

  const eventHighlightsOpts = formData.eventHighlights.map(t => ({
    key: t._id,
    refKey: t._id,
    item: t,
  }));

  const formHtml = (
    <div className="grid grid-cols-3 gap-4">
      <BubbleFormGroup label="Name" error={formData._errors.name} className="col-span-3" asterisk>
        <BubbleInput
          block
          type="text"
          value={formData.name}
          onValue={val => changeForm('name', val)}
        />
      </BubbleFormGroup>
      <div className="col-span-3">
        <BubbleFileM label="Logo" files={files} onUploadFile={onUploadFile} />
        <div className="italic text-slate mt-1">
          The recommended specs: PNG of size 230x130 pixels for wide logos, 230x230 pixels for
          square logos
        </div>
      </div>
      <BubbleFormGroup label="Segments" className="col-span-3">
        <BubbleTypeahead
          block
          options={segmentOpts}
          placeholder="Enter segment name..."
          menuClassName="max-h-80 overflow-y-auto"
          allowNew
          noSuggestionsClick
          alwaysShowSuggestions={segmentOpts.length > 0}
          suggestionsText="No similar segments found"
          suggestionClassName="p-1-i"
          suggestionsRenderFn={(_, opt) => (
            <div className="flex items-center">
              <BubbleInput
                type="text"
                block
                appearance="bare"
                value={opt.item.name}
                className="flex-grow mr-3"
                inputClassName="px-3 py-1 rounded"
                onValue={val => {
                  const arIdx = formData.segments.findIndex(s => s._id === opt.item._id);
                  if (arIdx < 0) return;
                  const newSegs = [...formData.segments];
                  newSegs.splice(arIdx, 1, { ...opt.item, name: val });
                  changeForm('segments', newSegs);
                }}
              />
              <span className="text-xs font-semibold uppercase text-navy mr-3">Color</span>
              <BubbleInput
                type="color"
                value={opt.item.color}
                onValue={val => {
                  const arIdx = formData.segments.findIndex(s => s._id === opt.item._id);
                  if (arIdx < 0) return;
                  const newSegs = [...formData.segments];
                  newSegs.splice(arIdx, 1, { ...opt.item, color: val });
                  changeForm('segments', newSegs);
                }}
              />
            </div>
          )}
          onCreate={val =>
            changeForm('segments', [
              ...formData.segments,
              { _id: new ObjectId().toHexString(), name: val, color: '#000000' },
            ])
          }
          onTrash={val =>
            changeForm(
              'segments',
              formData.segments.filter(ar => ar._id !== val.item._id)
            )
          }
        />
      </BubbleFormGroup>
      <BubbleFormGroup label="Levels" className="col-span-3">
        <BubbleTypeahead
          block
          options={levelOpts}
          placeholder="Enter level name..."
          menuClassName="max-h-80 overflow-y-auto"
          allowNew
          noSuggestionsClick
          alwaysShowSuggestions={levelOpts.length > 0}
          suggestionsText="No similar levels found"
          suggestionClassName="p-1-i"
          suggestionsRenderFn={(_, opt) => (
            <div className="flex items-center">
              <BubbleInput
                type="text"
                block
                appearance="bare"
                value={opt.item.name}
                className="flex-grow"
                inputClassName="px-3 py-1 rounded"
                onValue={val => {
                  const lIdx = formData.levels.findIndex(l => l._id === opt.item._id);
                  if (lIdx < 0) return;
                  const newLevels = [...formData.levels];
                  newLevels.splice(lIdx, 1, { ...opt.item, name: val });
                  changeForm('levels', newLevels);
                }}
              />
            </div>
          )}
          onCreate={val =>
            changeForm('levels', [
              ...formData.levels,
              { _id: new ObjectId().toHexString(), name: val },
            ])
          }
          onTrash={val =>
            changeForm(
              'levels',
              formData.levels.filter(ar => ar._id !== val.item._id)
            )
          }
        />
      </BubbleFormGroup>
      <BubbleFormGroup label="Attributes" className="col-span-3">
        <BubbleTypeahead
          block
          options={attributeOpts}
          placeholder="Enter attribue name..."
          menuClassName="max-h-80 overflow-y-auto"
          allowNew
          noSuggestionsClick
          alwaysShowSuggestions={attributeOpts.length > 0}
          suggestionsText="No similar attributes found"
          suggestionClassName="p-1-i"
          suggestionsRenderFn={(_, opt) => (
            <div className="flex items-center">
              <BubbleInput
                type="text"
                block
                appearance="bare"
                value={opt.item.name}
                className="flex-grow"
                inputClassName="px-3 py-1 rounded"
                onValue={val => {
                  const attrIdx = formData.attributes.findIndex(a => a._id === opt.item._id);
                  if (attrIdx < 0) return;
                  const newAttrs = [...formData.attributes];
                  newAttrs.splice(attrIdx, 1, { ...opt.item, name: val });
                  changeForm('attributes', newAttrs);
                }}
              />
            </div>
          )}
          onCreate={val =>
            changeForm('attributes', [
              ...formData.attributes,
              { _id: new ObjectId().toHexString(), name: val },
            ])
          }
          onTrash={val =>
            changeForm(
              'attributes',
              formData.attributes.filter(ar => ar._id !== val.item._id)
            )
          }
        />
      </BubbleFormGroup>
      <BubbleFormGroup
        label="Event Highlights"
        error={formData._errors.eventHighlights}
        className="col-span-3"
      >
        <BubbleTypeahead
          block
          options={eventHighlightsOpts}
          placeholder="Enter event hightlight name..."
          menuClassName="max-h-80 overflow-y-auto"
          allowNew
          noSuggestionsClick
          alwaysShowSuggestions={eventHighlightsOpts.length > 0}
          suggestionsText="No similar event hightlights found"
          suggestionClassName="p-1-i"
          suggestionsRenderFn={(_, opt) => (
            <div className="flex items-center">
              <BubbleInput
                type="text"
                block
                appearance="bare"
                value={opt.item.name}
                className="flex-grow"
                inputClassName="px-3 py-1 rounded"
                onValue={val => {
                  const eventHighlightIdx = formData.eventHighlights.findIndex(
                    s => s._id === opt.item._id
                  );
                  if (eventHighlightIdx < 0) return;
                  const newEventHighlight = [...formData.eventHighlights];
                  newEventHighlight.splice(eventHighlightIdx, 1, { ...opt.item, name: val });
                  changeForm('eventHighlights', newEventHighlight);
                }}
              />
            </div>
          )}
          onCreate={val => {
            const exists = formData.eventHighlights.find(s => s.name === val);
            if (!exists) {
              changeForm('eventHighlights', [
                ...formData.eventHighlights,
                {
                  _id: new ObjectId().toHexString(),
                  name: val,
                },
              ]);
            }
          }}
          onTrash={val =>
            changeForm(
              'eventHighlights',
              formData.eventHighlights.filter(ar => ar._id !== val.item._id)
            )
          }
        />
      </BubbleFormGroup>

      <BubbleFormGroup label="Venue Types" className="col-span-3">
        <BubbleTypeahead
          block
          options={venueTypeOpts}
          placeholder="Enter venue type name..."
          menuClassName="max-h-80 overflow-y-auto"
          allowNew
          noSuggestionsClick
          alwaysShowSuggestions={venueTypeOpts.length > 0}
          suggestionsText="No similar venue types found"
          suggestionClassName="p-1-i"
          suggestionsRenderFn={(_, opt) => (
            <div className="flex items-center">
              <BubbleInput
                type="text"
                block
                appearance="bare"
                value={opt.item.name}
                className="flex-grow mr-3"
                inputClassName="px-3 py-1 rounded"
                onValue={val => {
                  const vtIdx = formData.venueTypes.findIndex(s => s._id === opt.item._id);
                  if (vtIdx < 0) return;
                  const newVTypes = [...formData.venueTypes];
                  newVTypes.splice(vtIdx, 1, { ...opt.item, name: val });
                  changeForm('venueTypes', newVTypes);
                }}
              />

              {serverInfo.digitalOnlyMcmEdps.map(edp => {
                const mcmVisible = evaluateMcmEndpoint('visible', edp, opt.item.mcm, undefined);

                return (
                  <BubbleButton
                    key={edp}
                    className="p-1-i mr-1"
                    variant={mcmVisible ? 'navy' : 'smoke'}
                    inverse
                    icon={mcmEndpointToIcon(edp, mcmVisible)}
                    onClick={() => {
                      const vtIdx = formData.venueTypes.findIndex(s => s._id === opt.item._id);
                      if (vtIdx < 0) return;
                      const newVTypes = [...formData.venueTypes];

                      newVTypes.splice(vtIdx, 1, {
                        ...opt.item,
                        mcm: toggleMcmEndpoint(
                          'visible',
                          edp,
                          serverInfo.mcmEndpoints,
                          opt.item.mcm,
                          undefined
                        ),
                      });
                      changeForm('venueTypes', newVTypes);
                    }}
                  />
                );
              })}
            </div>
          )}
          onCreate={val =>
            changeForm('venueTypes', [
              ...formData.venueTypes,
              { _id: new ObjectId().toHexString(), name: val },
            ])
          }
          onTrash={val =>
            changeForm(
              'venueTypes',
              formData.venueTypes.filter(ar => ar._id !== val.item._id)
            )
          }
        />
        <div className="italic text-slate mt-1 flex">
          <span className="mr-3">Legend:</span>
          {serverInfo.digitalOnlyMcmEdps.map(edp => (
            <div key={edp} className="mr-3">
              <FontAwesomeIcon fixedWidth icon={mcmEndpointToIcon(edp, true)} />
              <span className="ml-1">{mcmEndpointToName(edp)}</span>
            </div>
          ))}
        </div>
      </BubbleFormGroup>
      <BubbleFormGroup label="Meal Periods" className="col-span-4">
        <BubbleTypeahead
          block
          options={mealPeriods}
          placeholder="Enter meal period name..."
          menuClassName="max-h-80 overflow-y-auto"
          allowNew
          noSuggestionsClick
          alwaysShowSuggestions={mealPeriods.length > 0}
          suggestionsText="No similar meal periods found"
          suggestionClassName="p-1-i"
          suggestionsRenderFn={(_, opt) => (
            <div className="flex items-center">
              <BubbleInput
                type="text"
                block
                placeholder="Name"
                appearance="bare"
                value={opt.item.name}
                className="flex-grow mr-3"
                inputClassName="px-3 py-1 rounded"
                onValue={val => {
                  const arIdx = formData.mealPeriods.findIndex(s => s._id === opt.item._id);
                  if (arIdx < 0) return;
                  const newMealPeriods = [...formData.mealPeriods];
                  newMealPeriods.splice(arIdx, 1, { ...opt.item, name: val });
                  changeForm('mealPeriods', newMealPeriods);
                }}
              />
              <BubbleInput
                type="text"
                placeholder="Code"
                block
                appearance="bare"
                value={opt.item.code}
                className="flex-grow mr-3"
                inputClassName="px-3 py-1 rounded"
                onValue={val => {
                  const arIdx = formData.mealPeriods.findIndex(s => s._id === opt.item._id);
                  if (arIdx < 0) return;
                  const newMealPeriods = [...formData.mealPeriods];
                  newMealPeriods.splice(arIdx, 1, { ...opt.item, code: val });
                  changeForm('mealPeriods', newMealPeriods);
                }}
              />
              <span className="text-xs font-semibold uppercase text-navy mr-3">Color</span>
              <BubbleInput
                type="color"
                value={opt.item.color}
                className="mr-2"
                onValue={val => {
                  const arIdx = formData.mealPeriods.findIndex(s => s._id === opt.item._id);
                  if (arIdx < 0) return;
                  const newMealPeriods = [...formData.mealPeriods];
                  newMealPeriods.splice(arIdx, 1, {
                    ...opt.item,
                    color: val,
                  });
                  changeForm('mealPeriods', newMealPeriods);
                }}
              />
              <div className="flex items-center">
                <div className="relative mr-3">
                  <BubbleInput
                    type="checkbox"
                    className="mr-1"
                    inputClassName="stretched-link"
                    checked={opt.item.type === 'sea'}
                    onValue={chk => {
                      if (chk) {
                        const arIdx = formData.mealPeriods.findIndex(s => s._id === opt.item._id);
                        if (arIdx < 0) return;
                        const newMealPeriods = [...formData.mealPeriods];
                        newMealPeriods.splice(arIdx, 1, {
                          ...opt.item,
                          type: 'sea',
                        });
                        changeForm('mealPeriods', newMealPeriods);
                      }
                    }}
                  />
                  <span>Ship</span>
                </div>
                <div className="relative">
                  <BubbleInput
                    type="checkbox"
                    className="mr-1"
                    inputClassName="stretched-link"
                    checked={opt.item.type === 'shore'}
                    onValue={chk => {
                      if (chk) {
                        const arIdx = formData.mealPeriods.findIndex(s => s._id === opt.item._id);
                        if (arIdx < 0) return;
                        const newMealPeriods = [...formData.mealPeriods];
                        newMealPeriods.splice(arIdx, 1, {
                          ...opt.item,
                          type: 'shore',
                        });
                        changeForm('mealPeriods', newMealPeriods);
                      }
                    }}
                  />
                  <span>Land</span>
                </div>
              </div>
            </div>
          )}
          onCreate={val =>
            changeForm('mealPeriods', [
              ...formData.mealPeriods,
              {
                _id: new ObjectId().toHexString(),
                code: '',
                type: 'sea',
                name: val,
                color: '#000000',
              },
            ])
          }
          onTrash={val =>
            changeForm(
              'mealPeriods',
              formData.mealPeriods.filter(ar => ar._id !== val.item._id)
            )
          }
        />
      </BubbleFormGroup>
      <BubbleFormGroup
        label="Group Types"
        error={formData._errors.groupTypes}
        className="col-span-3"
      >
        <BubbleTypeahead
          block
          options={groupTypeOpts}
          placeholder="Enter group type name..."
          menuClassName="max-h-80 overflow-y-auto"
          allowNew
          noSuggestionsClick
          alwaysShowSuggestions={groupTypeOpts.length > 0}
          suggestionsText="No similar group types found"
          suggestionClassName="p-1-i"
          suggestionsRenderFn={(_, opt) => (
            <div className="flex items-center">
              <BubbleInput
                type="text"
                block
                appearance="bare"
                value={opt.item.name}
                className="flex-grow"
                inputClassName="px-3 py-1 rounded"
                onValue={val => {
                  const groupTypeIdx = formData.groupTypes.findIndex(s => s._id === opt.item._id);
                  if (groupTypeIdx < 0) return;
                  const newGroupTypes = [...formData.groupTypes];
                  newGroupTypes.splice(groupTypeIdx, 1, { ...opt.item, name: val });
                  changeForm('groupTypes', newGroupTypes);
                }}
              />
            </div>
          )}
          onCreate={val => {
            const exists = formData.groupTypes.find(s => s.name === val);
            if (!exists) {
              changeForm('groupTypes', [
                ...formData.groupTypes,
                {
                  _id: new ObjectId().toHexString(),
                  name: val,
                },
              ]);
            }
          }}
          onTrash={val =>
            changeForm(
              'groupTypes',
              formData.groupTypes.filter(ar => ar._id !== val.item._id)
            )
          }
        />
      </BubbleFormGroup>
      <BubbleFormGroup
        label="Image Types"
        error={formData._errors.imageTypes}
        className="col-span-3"
      >
        <BubbleTypeahead
          block
          options={imageTypesOpts}
          placeholder="Enter image type name..."
          menuClassName="max-h-80 overflow-y-auto"
          allowNew
          noSuggestionsClick
          alwaysShowSuggestions={imageTypesOpts.length > 0}
          suggestionsText="No similar image types found"
          suggestionClassName="p-1-i"
          suggestionsRenderFn={(_, opt) => (
            <div className="flex items-center">
              <BubbleInput
                type="text"
                block
                appearance="bare"
                value={opt.item.name}
                className="flex-grow"
                inputClassName="px-3 py-1 rounded"
                onValue={val => {
                  const imageTypeIdx = formData.imageTypes.findIndex(s => s._id === opt.item._id);
                  if (imageTypeIdx < 0) return;
                  const newImageTypes = [...formData.imageTypes];
                  newImageTypes.splice(imageTypeIdx, 1, { ...opt.item, name: val });
                  changeForm('imageTypes', newImageTypes);
                }}
              />
            </div>
          )}
          onCreate={val => {
            const exists = formData.imageTypes.find(s => s.name === val);
            if (!exists) {
              changeForm('imageTypes', [
                ...formData.imageTypes,
                {
                  _id: new ObjectId().toHexString(),
                  name: val,
                },
              ]);
            }
          }}
          onTrash={val =>
            changeForm(
              'imageTypes',
              formData.imageTypes.filter(ar => ar._id !== val.item._id)
            )
          }
        />
      </BubbleFormGroup>

      <BubbleFormGroup label="Day Themes" error={formData._errors.dayThemes} className="col-span-3">
        <BubbleTypeahead
          block
          options={dayThemeOpts}
          placeholder="Enter day theme name..."
          menuClassName="max-h-80 overflow-y-auto"
          allowNew
          noSuggestionsClick
          alwaysShowSuggestions={dayThemeOpts.length > 0}
          suggestionsText="No similar day themes found"
          suggestionClassName="p-1-i"
          suggestionsRenderFn={(_, opt) => (
            <div className="flex items-center">
              <BubbleInput
                type="text"
                block
                appearance="bare"
                value={opt.item.name}
                className="flex-grow"
                inputClassName="px-3 py-1 rounded"
                onValue={val => {
                  const dayThemeIdx = formData.dayThemes.findIndex(s => s._id === opt.item._id);
                  if (dayThemeIdx < 0) return;
                  const newDayThemes = [...formData.dayThemes];
                  newDayThemes.splice(dayThemeIdx, 1, { ...opt.item, name: val });
                  changeForm('dayThemes', newDayThemes);
                }}
              />
            </div>
          )}
          onCreate={val => {
            const exists = formData.dayThemes.find(s => s.name === val);
            if (!exists) {
              changeForm('dayThemes', [
                ...formData.dayThemes,
                {
                  _id: new ObjectId().toHexString(),
                  name: val,
                },
              ]);
            }
          }}
          onTrash={val =>
            changeForm(
              'dayThemes',
              formData.dayThemes.filter(ar => ar._id !== val.item._id)
            )
          }
        />
      </BubbleFormGroup>

      <BubbleFormGroup
        label="Time Groupings"
        error={formData._errors.timeGroupings}
        className="col-span-3"
      >
        <BubbleTypeahead
          block
          options={timeGroupingOpts}
          placeholder="Enter time grouping name..."
          menuClassName="max-h-80 overflow-y-auto"
          allowNew
          noSuggestionsClick
          alwaysShowSuggestions={timeGroupingOpts.length > 0}
          suggestionsText="No similar time groupings found"
          suggestionClassName="p-1-i"
          suggestionsRenderFn={(_, opt) => (
            <div className="flex items-center">
              <BubbleInput
                type="text"
                block
                appearance="bare"
                value={opt.item.name}
                className="flex-grow"
                inputClassName="px-3 py-1 rounded"
                onValue={val => {
                  const timeGroupingIdx = formData.timeGroupings.findIndex(
                    s => s._id === opt.item._id
                  );
                  if (timeGroupingIdx < 0) return;
                  const newTimeGroupings = [...formData.timeGroupings];
                  newTimeGroupings[timeGroupingIdx].name = val;
                  changeForm('timeGroupings', newTimeGroupings);
                }}
              />
            </div>
          )}
          onCreate={val => {
            const exists = formData.timeGroupings.find(s => s.name === val);
            if (!exists) {
              changeForm('timeGroupings', [
                ...formData.timeGroupings,
                {
                  _id: new ObjectId().toHexString(),
                  name: val,
                },
              ]);
            }
          }}
          onTrash={val =>
            changeForm(
              'timeGroupings',
              formData.timeGroupings.filter(ar => ar._id !== val.item._id)
            )
          }
        />
        <div className="italic text-slate mt-1">
          These options populate the Time Grouping dropdown on the Event Rundown widget.
        </div>
      </BubbleFormGroup>
      <BubbleFormGroup
        label="Rundown Font Size"
        className="col-span-3"
        error={formData._errors.fontSize}
      >
        <div className="flex items-center">
          <div className="relative mr-3">
            <span>MIN </span>
            <BubbleInput
              min={RUNDOWN_LIMIT_FONT_SIZE.min}
              max={RUNDOWN_LIMIT_FONT_SIZE.max}
              type="number"
              className="mr-1"
              value={formData.fontSize.min}
              onValue={val => changeForm('fontSize', { min: val, max: formData.fontSize.max })}
            />
          </div>
          <div className="relative">
            <span>MAX </span>
            <BubbleInput
              min={RUNDOWN_LIMIT_FONT_SIZE.min}
              max={RUNDOWN_LIMIT_FONT_SIZE.max}
              type="number"
              className="mr-1"
              value={formData.fontSize.max}
              onValue={val => changeForm('fontSize', { min: formData.fontSize.min, max: val })}
            />
          </div>
        </div>
        <div className="italic text-slate mt-1">
          The limit values for Rundown are: MIN={RUNDOWN_LIMIT_FONT_SIZE.min} and MAX=
          {RUNDOWN_LIMIT_FONT_SIZE.max}
        </div>
      </BubbleFormGroup>

      <BubbleFormGroup label="Additional Instance Details" className="col-span-3">
        <BubbleTypeahead
          block
          options={notesOpts}
          placeholder="Enter note name..."
          menuClassName="max-h-80 overflow-y-auto"
          allowNew
          noSuggestionsClick
          alwaysShowSuggestions={notesOpts.length > 0}
          suggestionsText="No similar notes found"
          suggestionClassName="p-1-i"
          suggestionsRenderFn={(_, opt) => (
            <div className="flex items-center">
              <span className="text-xs font-semibold uppercase text-navy mx-3">Label</span>
              <BubbleInput
                type="text"
                block
                appearance="bare"
                value={opt.item.label}
                className="flex-grow mr-3"
                inputClassName="px-3 py-1 rounded"
                onValue={val => {
                  const nIdx = formData.settings.notes.findIndex(n => n._id === opt.item._id);
                  if (nIdx < 0) return;
                  const newNotes = [...formData.settings.notes];
                  newNotes.splice(nIdx, 1, { ...opt.item, label: val });
                  changeForm('settings', { ...formData.settings, notes: newNotes });
                }}
              />
              <span className="text-xs font-semibold uppercase text-navy mr-3">Value</span>
              <BubbleInput
                type="text"
                block
                appearance="bare"
                value={opt.item.value}
                className="flex-grow"
                inputClassName="px-3 py-1 rounded"
                onValue={val => {
                  const nIdx = formData.settings.notes.findIndex(n => n._id === opt.item._id);
                  if (nIdx < 0) return;
                  const newNotes = [...formData.settings.notes];
                  newNotes.splice(nIdx, 1, { ...opt.item, value: val });
                  changeForm('settings', { ...formData.settings, notes: newNotes });
                }}
              />
            </div>
          )}
          onCreate={val =>
            changeForm('settings', {
              ...formData.settings,
              notes: [
                ...formData.settings.notes,
                { _id: new ObjectId().toHexString(), label: val, value: '' },
              ],
            })
          }
          onTrash={val =>
            changeForm('settings', {
              ...formData.settings,
              notes: formData.settings.notes.filter(n => n._id !== val.item._id),
            })
          }
        />
      </BubbleFormGroup>
    </div>
  );

  const formActions = (
    <div className="flex items-center">
      <div className="ml-auto" />
      <BubbleButton
        variant="navy"
        onClick={resetForm}
        inverse
        disabled={!formData._dirty || errored || saving}
        icon="rotate-left"
      >
        Reset
      </BubbleButton>
      <BubbleButton
        variant="navy"
        onClick={submitForm}
        disabled={!formData._dirty || errored || saving}
        icon="floppy-disk"
      >
        Save Changes
      </BubbleButton>
    </div>
  );

  const portalButtons = props.actionsElem ? createPortal(formActions, props.actionsElem) : <></>;

  return (
    <>
      {formHtml}
      {portalButtons}
    </>
  );
}
